import { ClipTypeEnum, Engine, EventsEnum, TextClip, UndoActionEnum, UndoRecord } from "@rendley/sdk";
import { Clip, Layer, MediaData, MediaDataStatusEnum, RendleyStore, Transition } from "./store/RendleyStore";
import { ApplicationStore } from "./store/ApplicationStore";
import { arrayToMap } from "./modules/timeline/utils/convertArrayToMap";
import { updateAdjacency } from "./modules/timeline/utils/updateLayerClipsAdjacency";
import { RendleyService } from "./services/RendleyService";
import { fontSources } from "./config/fonts.config";
import { UndoLocalActionsEnum } from "./types/undo.types";
import { EventPayload } from "./types/rendley.types";
import { getStepsOrderByClipId } from "./modules/controls-sidebar/helpers/getStepsOrderByClipId";
import { SidebarStore } from "./store/SidebarStore";
import {CLIP_LIBRETTO_CLIP_ID, GenerateLibrettoClipId} from "./Constants";
// import { fontSources } from "./config/fonts.config";

class RendleyBridge {
    async init(options: {
        transitionsPath: string;
        filtersPath: string;
        effectsPath: string;
        titlesPath: string;
        subtitlesStylesPath: string;
        animationsPath: string;
    }) {
        try {
            this.setupEventListeners();

            ApplicationStore.setTitlesPath(options.titlesPath);
            ApplicationStore.setSubtitleStylesPath(options.subtitlesStylesPath);
            ApplicationStore.setAnimationsPath(options.animationsPath);

            const subtitlesManager = Engine.getInstance().getSubtitlesManager();
            RendleyStore.setSubtitlesHighlightStyle(subtitlesManager.getHighlightTextStyle());
            RendleyStore.setSubtitlesMainTextStyle(subtitlesManager.getMainTextStyle());

            try {
                // Load fonts in the background
                await Promise.all(
                    fontSources.map(async ({ family, cssUrl }) => {
                        return Engine.getInstance().getFontRegistry().loadFromCssUrl(family, cssUrl);
                    }),
                );
            } catch (err) {
                console.error("Failed to load fonts!", err);
            }

            const transitionsRoot = options.transitionsPath;
            const filtersRoot = options.filtersPath;
            const effectsRoot = options.effectsPath;

            // Get transition descriptions
            fetch(transitionsRoot + "transitions.json")
                .then((data) => data.json())
                .then((data) => {
                    const transitions = data.transitions.map((transition: any) => {
                        return {
                            id: transition.id,
                            label: transition.name,
                            thumbnailUrl: transitionsRoot + transition.path + "thumbnail.webp",
                            videoPreviewUrl: transitionsRoot + transition.path + "preview.mp4",
                            shaderUrl: transitionsRoot + transition.path + "shader.glsl",
                            properties: transition.properties,
                        };
                    });

                    ApplicationStore.setTransitions(arrayToMap(transitions, "id"));
                });

            // Get filter list
            fetch(filtersRoot + "filters.json")
                .then((data) => data.json())
                .then((data) => {
                    const filters = data.filters.map((filter: any) => {
                        return {
                            id: filter.id,
                            label: filter.name,
                            thumbnailUrl: filtersRoot + filter.path + "thumbnail.webp",
                            lutUrl: filtersRoot + filter.path + "lut.png",
                        };
                    });

                    ApplicationStore.setFilters(arrayToMap(filters, "id"));
                });

            // Get Effect list
            fetch(effectsRoot + "effects.json")
                .then((data) => data.json())
                .then((data) => {
                    const effects = data.effects.map((effect: any) => {
                        return {
                            id: effect.id,
                            label: effect.name,
                            thumbnailUrl: effectsRoot + effect.path + "thumbnail.webp",
                            videoPreviewUrl: effectsRoot + effect.path + "preview.mp4",
                            shaderUrl: effectsRoot + effect.path + "shader.glsl",
                            properties: effect.properties ?? [], // ?? for backward compatibility as properties didn't exist in previous json
                        };
                    });

                    const builtinEffects = data.builtin_effects?.map((effect: any) => {
                        return {
                            id: effect.id,
                            label: effect.name,
                            thumbnailUrl: effectsRoot + effect.path + "thumbnail.webp",
                            videoPreviewUrl: effectsRoot + effect.path + "preview.mp4",
                        };
                    });

                    if (builtinEffects) {
                        effects.push(...builtinEffects);
                    }

                    ApplicationStore.setEffects(arrayToMap(effects, "id"));
                });
        } finally {
            ApplicationStore.setIsLoading(false);
        }

        // TODO: Remove on production!
        (window as any).engine = Engine.getInstance();
    }

    private setupEventListeners() {
        const events = Engine.getInstance().events;

        events.on(EventsEnum.PLAYING, this.handlePlaying);
        events.on(EventsEnum.TIME, this.handleTime);
        events.on(EventsEnum.READY, this.handleReady);
        events.on(EventsEnum.DISPLAY_BACKGROUND_UPDATED, this.handleBackgroundUpdated);
        events.on(EventsEnum.DISPLAY_RESOLUTION_UPDATED, this.handleResolutionUpdated);
        events.on(EventsEnum.LAYER_ADDED, this.handleLayerAdded);
        events.on(EventsEnum.LAYER_REMOVED, this.handleLayerRemoved);
        events.on(EventsEnum.LAYER_UPDATED, this.handleLayerUpdated);
        events.on(EventsEnum.LAYERS_ORDER_UPDATED, this.handleLayersOrderUpdated);
        events.on(EventsEnum.TRANSITION_ADDED, this.handleAddTransition);
        events.on(EventsEnum.TRANSITION_REMOVED, this.handleRemoveTransition);
        events.on(EventsEnum.CLIP_ADDED, this.handleClipAdded);
        events.on(EventsEnum.CLIP_REMOVED, this.handleClipRemoved);
        events.on(EventsEnum.CLIP_UPDATED, this.handleClipUpdated);
        events.on(EventsEnum.CLIP_UPDATED_TEXT, this.handleClipUpdatedText);
        events.on(EventsEnum.CLIP_STYLE_UPDATED, this.handleClipStyleUpdated);
        events.on(EventsEnum.CLIP_ERROR, this.handleClipError);
        events.on(EventsEnum.LIBRARY_ADDED, this.handleLibraryAdded);
        events.on(EventsEnum.LIBRARY_REMOVED, this.handleLibraryRemoved);
        events.on(EventsEnum.LIBRARY_ERROR, this.handleLibraryError);
        events.on(EventsEnum.LIBRARY_MEDIA_UPDATED, this.handleLibraryMediaUpdated);
        events.on(EventsEnum.LIBRARY_REPLACED, this.handleLibraryMediaReplaced);
        events.on(EventsEnum.UNDO_REDO_CHANGED, this.handleUndoRedoChanged);
        events.on(EventsEnum.UNDO_PERFORMED, this.handleUndoRedoPerformed);
        events.on(EventsEnum.REDO_PERFORMED, this.handleUndoRedoPerformed);
        events.on(EventsEnum.UNDO_PROCESS_CUSTOM, this.handleUndoProcessCustom);
        events.on(EventsEnum.SUBTITLES_HIGHLIGHTED_STYLE_UPDATED, this.handleHighlightStyleUpdated);
        events.on(EventsEnum.SUBTITLES_MAIN_STYLE_UPDATED, this.handleMainStyleUpdated);
    }

    destroy() {
        Engine.getInstance().events.removeAllListeners();
    }

    private handleReady = () => {
        const timeline = Engine.getInstance().getTimeline();
        const display = Engine.getInstance().getDisplay();
        const resolution = display.getResolution();

        const media = Object.values(Engine.getInstance().getLibrary().media).reduce<Record<string, MediaData>>(
            (acc, cv) => {
                const data = {
                    id: cv.getId(),
                    thumbnail: cv.thumbnail,
                    type: cv.type,
                    filename: cv.filename,
                    duration: cv.duration,
                    status: MediaDataStatusEnum.READY,
                };

                acc[data.id] = data;
                return acc;
            },
            {},
        );

        const clips = timeline.getClips().reduce<Record<string, Clip>>((acc, cv) => {
            const data: Clip = {
                id: cv.id,
                type: cv.getType() as ClipTypeEnum,
                startTime: cv.getStartTime(),
                leftTrim: cv.getLeftTrim(),
                rightTrim: cv.getRightTrim(),
                duration: cv.getDuration(),
                trimmedDuration: cv.getTrimmedDuration(),
                mediaDataId: cv.getMediaId(),
                text: cv instanceof TextClip ? cv.text : "",
            };

            acc[data.id] = data;
            return acc;
        }, {});

        const layers = Object.values(timeline.layers).reduce<Record<string, Layer>>((acc, cv) => {
            const data: Layer = {
                id: cv.id,
                clipsIds: cv.clipsIds,
                transitionIds: cv.transitions.map((t) => t.getId()),
            };

            acc[data.id] = data;
            return acc;
        }, {});

        const transitions = Object.values(timeline.layers)
            .map((layer) => Object.values(layer.transitions))
            .flat()
            .reduce<Record<string, Transition>>((acc, cv) => {
                const data: Transition = {
                    id: cv.getId(),
                    startClipId: cv.getStartClipId(),
                    endClipId: cv.getEndClipId(),
                    inDuration: cv.getInDuration(),
                    outDuration: cv.getOutDuration(),
                };

                acc[data.id] = data;
                return acc;
            }, {});

        const styles = timeline.getClips().reduce<Record<string, any>>((acc, cv) => {
            const data = cv.style && Object.keys(cv.style).length > 0 ? cv.style.serialize() : undefined;

            acc[cv.id] = data;
            return acc;
        }, {});

        ApplicationStore.setSelectedTransitionId(null);

        RendleyStore.setIsPlaying(false);
        RendleyStore.setCurrentTime(timeline.currentTime);
        RendleyStore.setDisplayResolution(resolution[0], resolution[1]);
        RendleyStore.setDisplayBackgroundColor(display.getBackgroundColor());
        RendleyStore.setMedia(media);
        RendleyStore.setClips(clips);
        RendleyStore.setLayers(layers);
        RendleyStore.setLayersOrder(timeline.layersOrder);
        RendleyStore.setTransitions(transitions);
        RendleyStore.setStyles(styles);
        RendleyStore.setUpdateTimestamp(Date.now());
        RendleyStore.updateTimelineDuration();

        RendleyStore.layersOrder.forEach((layerId) => {
            updateAdjacency(layerId);
        });
    };

    private handlePlaying = (payload: EventPayload<EventsEnum.PLAYING>) => {
        RendleyStore.setIsPlaying(payload.isPlaying);
    };

    private handleTime = (currentTime: number) => {
        const fitDuration = RendleyService.getEngine().getTimeline().getFitDuration();
        const stopAt = Math.max(0, fitDuration - 1 / Engine.getInstance().getTimeline().getFps());

        if (currentTime >= stopAt && RendleyStore.isPlaying) {
            RendleyService.pause();
            RendleyService.seek(stopAt);
            return;
        }

        RendleyStore.setCurrentTime(currentTime);
    };

    private handleBackgroundUpdated = (payload: EventPayload<EventsEnum.DISPLAY_BACKGROUND_UPDATED>) => {
        RendleyStore.setDisplayBackgroundColor(payload.backgroundColor);
    };

    private handleResolutionUpdated = (payload: EventPayload<EventsEnum.DISPLAY_RESOLUTION_UPDATED>) => {
        RendleyStore.setDisplayResolution(payload.width, payload.height);
    };

    private handleAddTransition = (payload: EventPayload<EventsEnum.TRANSITION_ADDED>) => {
        const layer = Engine.getInstance().getTimeline().getLayerById(payload.layerId);
        if (!layer) return;

        const transition = layer.transitions.find((tx) => tx.getId() === payload.transitionId);
        if (!transition) return;

        RendleyStore.addTransition(transition.serialize());

        RendleyStore.updateLayer(layer.id, {
            transitionIds: [...RendleyStore.layers[layer.id].transitionIds, transition.getId()],
        });
    };

    private handleRemoveTransition = (payload: EventPayload<EventsEnum.TRANSITION_REMOVED>) => {
        ApplicationStore.setSelectedTransitionId(null);
        RendleyStore.deleteTransition(payload.transitionId, payload.layerId);
    };

    private handleLayerUpdated = (payload: EventPayload<EventsEnum.LAYER_UPDATED>) => {
        const layer = RendleyService.getLayerById(payload.layerId);
        if (!layer) return;

        RendleyStore.updateLayer(layer.id, {
            clipsIds: layer.clipsIds,
            transitionIds: layer.transitions.map((tx) => tx.getId()),
        });

        // If this is triggered during a undo group don't delete the layer as it will cause trouble, the layer creation is registered in the undo sequence
        if (layer?.clipsIds?.length === 0 && !Engine.getInstance().getUndoManager().getPerforming()) {
            RendleyService.deleteLayer(payload.layerId);
        }
    };

    private handleLayerAdded = (payload: EventPayload<EventsEnum.LAYER_ADDED>) => {
        const timeline = Engine.getInstance().getTimeline();
        const layer = timeline.layers[payload.layerId];
        if (!layer) return;

        RendleyStore.addLayer({
            id: layer.id,
            clipsIds: layer.clipsIds,
            transitionIds: layer.transitions.map((tx) => tx.getId()),
        });

        RendleyStore.setLayersOrder(timeline.layersOrder);
    };

    private handleLayerRemoved = (payload: EventPayload<EventsEnum.LAYER_REMOVED>) => {
        RendleyStore.deleteLayer(payload.layerId);
    };

    private handleLayersOrderUpdated = (payload: EventPayload<EventsEnum.LAYERS_ORDER_UPDATED>) => {
        RendleyStore.setLayersOrder(Engine.getInstance().getTimeline().layersOrder);
    };

    private handleClipUpdated = (payload: EventPayload<EventsEnum.CLIP_UPDATED>) => {
        const clip = Engine.getInstance().getClipById(payload.clipId);
        if (!clip) return;

        RendleyStore.updateClip(payload.clipId, {
            startTime: clip.getStartTime(),
            duration: clip.getDuration(),
            leftTrim: clip.getLeftTrim(),
            rightTrim: clip.getRightTrim(),
            trimmedDuration: clip.getTrimmedDuration(),
        });

        window.dispatchEvent(new CustomEvent("LIBRETTO_CLIP_UPDATED", {
            detail: {
                layerId: ""
            }
        }));
    };

    private handleClipStyleUpdated = (payload: EventPayload<EventsEnum.CLIP_STYLE_UPDATED>) => {
        RendleyStore.updateStyles(payload.clipId, {
            [payload.property]: payload.value,
        });
    };

    private handleClipUpdatedText = (payload: EventPayload<EventsEnum.CLIP_UPDATED_TEXT>) => {
        RendleyStore.updateClip(payload.clipId, {
            text: payload.text,
        });
    };

    private handleClipAdded = (payload: EventPayload<EventsEnum.CLIP_ADDED>) => {
        const clip = Engine.getInstance().getTimeline().getClipById(payload.clipId);
        const clipId = clip?.getId() ?? null;
        const stepsOrder = getStepsOrderByClipId(clipId);

        const librettoClipId = RendleyStore.getLibrettoClipByClipId(clipId);

        ApplicationStore.setSelectedLibrettoClipId(librettoClipId);

        // Set the first step as active
        SidebarStore.setActiveControlItemKey(stepsOrder[0]);

        if (!clip) {
            return;
        }

        const clipInstance = Engine.getInstance().getClipById(payload.clipId);

        RendleyStore.setClip({
            id: clip.id,
            type: (clip as any).type,
            text: clip instanceof TextClip ? clip.text : "",
            startTime: clip.getStartTime(),
            leftTrim: clip.getLeftTrim(),
            rightTrim: clip.getRightTrim(),
            duration: clip.getDuration(),
            trimmedDuration: clip.getTrimmedDuration(),
            mediaDataId: clip.getMediaId(),
        });

        const styles = clipInstance?.style?.serialize?.();

        if (styles) {
            RendleyStore.updateStyles(clip.id, styles);
        }

        RendleyStore.updateTimelineDuration();

        window.dispatchEvent(new CustomEvent("LIBRETTO_CLIP_ADDED", {
            detail: {
                layerId: "",
            }
        }));
    };

    private handleClipRemoved = (payload: EventPayload<EventsEnum.CLIP_REMOVED>) => {
        RendleyStore.deleteClip(payload.clipId, payload.layerId);

        const layer = RendleyStore.layers[payload.layerId];

        // If this is triggered during a undo group don't delete the layer as it will cause trouble, the layer creation is registered in the undo sequence
        if (layer?.clipsIds?.length === 0 && !Engine.getInstance().getUndoManager().getPerforming()) {
            RendleyService.deleteLayer(payload.layerId);
        } else {
            updateAdjacency(payload.layerId);
        }

        window.dispatchEvent(new CustomEvent("LIBRETTO_CLIP_REMOVED", {
            detail: {
                layerId: payload.layerId,
            }
        }));
    };

    private handleLibraryAdded = (payload: EventPayload<EventsEnum.LIBRARY_ADDED>) => {
        const mediaData = Engine.getInstance().getLibrary().getMediaById(payload.mediaDataId);

        if (mediaData == null) {
            return;
        }

        RendleyStore.addMedia({
            id: mediaData.getId(),
            thumbnail: mediaData.thumbnail,
            type: mediaData.type,
            filename: mediaData.filename,
            duration: mediaData.duration,
            status: MediaDataStatusEnum.LOADING,
        });

        const mediaWaveformData = RendleyService.getMediaById(mediaData.getId()).getCustomData("waveformData") as Float32Array;

        RendleyStore.addWaveform({
            id: mediaData.getId(),
            duration: mediaData.duration,
            data: mediaWaveformData
        })
    };

    private handleLibraryRemoved = (payload: EventPayload<EventsEnum.LIBRARY_REMOVED>) => {
        RendleyStore.deleteMedia(payload.mediaDataId);
        RendleyStore.deleteWaveform(payload.mediaDataId);
    };

    private handleLibraryMediaUpdated = (payload: EventPayload<EventsEnum.LIBRARY_MEDIA_UPDATED>) => {
        if (payload.status === "ready") {
            this.handleLibraryMediaReplaced(payload);
        }
    };

    private handleLibraryMediaReplaced = (payload: EventPayload<EventsEnum.LIBRARY_REPLACED>) => {
        const mediaData = RendleyService.getMediaById(payload.mediaDataId);

        if (mediaData == null) {
            return;
        }

        RendleyStore.addMedia({
            id: mediaData.getId(),
            thumbnail: mediaData.thumbnail,
            type: mediaData.type,
            filename: mediaData.filename,
            duration: mediaData.duration,
            status: MediaDataStatusEnum.READY,
        });
    };

    private handleLibraryError = (payload: EventPayload<EventsEnum.LIBRARY_ERROR>) => {
        console.error("Library Error:", payload);
    };

    private handleClipError = (payload: EventPayload<EventsEnum.CLIP_ERROR>) => {
        console.error("Clip Error:", payload);
    };

    private handleUndoRedoChanged = (payload: EventPayload<EventsEnum.UNDO_REDO_CHANGED>) => {
        RendleyStore.setUndoRedoState(payload.canUndo, payload.canRedo);
    };

    private handleUndoRedoPerformed = (payload: EventPayload<EventsEnum.UNDO_PERFORMED>) => {
        // payload;
        const layerIds = Object.values(RendleyStore.layers)
            .filter((layer) => layer?.clipsIds?.length === 0)
            .map((layer) => layer.id);
        layerIds.forEach((layerId) => RendleyService.deleteLayer(layerId));
    };

    private handleUndoProcessCustom = (payload: EventPayload<EventsEnum.UNDO_PROCESS_CUSTOM>) => {
        switch (payload.undoRecord.action as string) {
            case UndoLocalActionsEnum.SET_SUBTITLES_MODE: {
                RendleyStore.setSubtitlesMode(payload.undoRecord.data.prev);

                payload.redoGroup.records.push(
                    new UndoRecord(UndoLocalActionsEnum.SET_SUBTITLES_MODE as unknown as UndoActionEnum, {
                        prev: payload.undoRecord.data.prev,
                        current: payload.undoRecord.data.current,
                    }),
                );
                break;
            }
            case UndoLocalActionsEnum.SET_SUBTITLES_STYLE: {
                const subtitlesManager = Engine.getInstance().getSubtitlesManager();

                subtitlesManager.setMainTextStyle(payload.undoRecord.data.prev.mainTextStyle, true);
                subtitlesManager.setHighlightedTextStyle(payload.undoRecord.data.prev.highlightTextStyle, true);
                RendleyStore.setSubtitlesStyle(payload.undoRecord.data.prev.styleId);

                payload.redoGroup.records.push(
                    new UndoRecord(UndoLocalActionsEnum.SET_SUBTITLES_STYLE as unknown as UndoActionEnum, {
                        prev: payload.undoRecord.data.prev,
                        current: payload.undoRecord.data.current,
                    }),
                );
                break;
            }
            case UndoLocalActionsEnum.SET_SUBTITLES_ANIMATION: {
                const subtitlesManager = Engine.getInstance().getSubtitlesManager();

                subtitlesManager.setHighlightAnimation(payload.undoRecord.data.prev.highlightAnimation);
                RendleyStore.setSubtitlesAnimation(payload.undoRecord.data.prev.animationId);

                payload.redoGroup.records.push(
                    new UndoRecord(UndoLocalActionsEnum.SET_SUBTITLES_ANIMATION as unknown as UndoActionEnum, {
                        prev: payload.undoRecord.data.prev,
                        current: payload.undoRecord.data.current,
                    }),
                );
                break;
            }
        }
    };

    private handleHighlightStyleUpdated = () => {
        const subtitlesManager = Engine.getInstance().getSubtitlesManager();

        RendleyStore.setSubtitlesHighlightStyle(subtitlesManager.getHighlightTextStyle());
    };

    private handleMainStyleUpdated = () => {
        const subtitlesManager = Engine.getInstance().getSubtitlesManager();

        RendleyStore.setSubtitlesMainTextStyle(subtitlesManager.getMainTextStyle());
    };
}

export default new RendleyBridge();